import { useEffect, useRef, useState } from "react";
import { Volume2, Square, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { voiceStatus, voiceTts, voiceLetterAudio } from "../lib/api";

let statusCache = null;
export const useVoiceReady = () => {
  const [ready, setReady] = useState(statusCache?.configured ?? false);
  useEffect(() => {
    if (statusCache) { setReady(statusCache.configured); return; }
    voiceStatus().then((s) => { statusCache = s; setReady(s.configured); }).catch(() => setReady(false));
  }, []);
  return ready;
};
export const resetVoiceStatus = () => { statusCache = null; };

// Plays MP3 fetched from `load()` (a promise of a Blob). Shown only when ElevenLabs is configured.
const Player = ({ load, label, testid, className = "" }) => {
  const [state, setState] = useState("idle");
  const audioRef = useRef(null);
  const stop = () => { audioRef.current?.pause(); audioRef.current = null; setState("idle"); };
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const play = async () => {
    if (state === "playing") return stop();
    setState("loading");
    try {
      const blob = await load();
      const a = new Audio(URL.createObjectURL(blob));
      audioRef.current = a;
      a.onended = stop;
      await a.play();
      setState("playing");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't play audio"); setState("idle"); }
  };
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Square : Volume2;
  return (
    <button type="button" data-testid={testid} onClick={play} title={label} className={`inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary ${className}`}>
      <Icon className={`h-3.5 w-3.5 ${state === "loading" ? "animate-spin" : ""}`} /> {state === "playing" ? "Stop" : label}
    </button>
  );
};

export const ReadAloudButton = ({ text, testid = "read-aloud-btn", className }) => {
  const ready = useVoiceReady();
  if (!ready || !text) return null;
  return <Player load={() => voiceTts(text)} label="Read aloud" testid={testid} className={className} />;
};

export const LetterAudioButton = ({ letterId, hasAudio, testid, title }) => {
  const ready = useVoiceReady();
  const [busy, setBusy] = useState(false);
  if (!ready || !hasAudio) return null;
  const download = async () => {
    setBusy(true);
    try {
      const url = URL.createObjectURL(await voiceLetterAudio(letterId));
      const a = document.createElement("a");
      a.href = url; a.download = `${(title || "letter").replace(/[^\p{L}\p{N}\s-]+/gu, "").trim() || "letter"}.mp3`;
      a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't download the audio"); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-3">
      <Player load={() => voiceLetterAudio(letterId)} label="Listen" testid={testid} />
      <button type="button" onClick={download} disabled={busy} title="Download the audio to keep offline" data-testid={`letter-download-${letterId}`} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download
      </button>
    </span>
  );
};
